import React from 'react';
import { Loader2 } from 'lucide-react';
import FormSkeleton from './FormSkeleton';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';

function normalizePolicyHref({ rawUrl, policyOrigin }) {
  if (!rawUrl || typeof rawUrl !== 'string') return '';

  const trimmed = rawUrl.trim();
  if (!trimmed) return '';

  // Prevent script injection in href.
  if (/^javascript:/i.test(trimmed)) return '';

  // Allow local absolute paths (served by backend) and pin them to policyOrigin.
  if (trimmed.startsWith('/')) {
    if (!policyOrigin) return trimmed;
    return `${policyOrigin}${trimmed}`;
  }

  // Already has a protocol we expect.
  if (/^https?:\/\//i.test(trimmed)) return trimmed;

  // Protocol-relative.
  if (/^\/\//.test(trimmed)) return `https:${trimmed}`;

  // Bare domain / www / etc.
  return `https://${trimmed.replace(/^\/+/, '')}`;
}

function slugifyBusinessName(name) {
  if (!name || typeof name !== 'string') return 'business';
  return (
    name
      .toLowerCase()
      .trim()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-{2,}/g, '-')
      .slice(0, 80) || 'business'
  );
}

const inputClassName =
  'border-brand text-base focus-visible:ring-brand sm:text-sm';
const consentRowClassName = 'flex items-start gap-3 py-3';
const consentCheckboxClassName =
  'mt-0.5 data-[state=checked]:bg-brand data-[state=checked]:text-brand-foreground focus-visible:ring-brand';
const consentTextClassName =
  'cursor-pointer whitespace-pre-line text-[0.82rem] font-normal leading-[1.25] text-muted-foreground';
const policyLinkClassName =
  'underline underline-offset-2 hover:text-foreground';

function CustomerForm({
  brandPromotionalMessage,
  supportOptIn,
  setSupportOptIn,
  marketingOptIn,
  setMarketingOptIn,
  termsAccepted,
  setTermsAccepted,
  handleNameChange,
  handlePhoneChange,
  handleEmailChange,
  nameError,
  phoneNumberError,
  emailError,
  name,
  email,
  phoneNumber,
  businessName,
  twilioNumber,
  handleSubmit,
  apiError,
  apiErrorMessage,
  formErrorMessage,
  errorColor,
  loading,
  isIncludingPhone,
  isIncludingEmail,
  termsOfServiceUrl,
  privacyPolicyUrl,
}) {
  const policyOrigin = (() => {
    try {
      const { hostname, port, origin } = window.location;
      const isLocalHost =
        hostname === 'localhost' || hostname === '127.0.0.1';
      // In local dev the React dev server runs on a different port (3000/3001),
      // but policy pages are served by the backend on :5010.
      if (isLocalHost && port && port !== '5010') {
        return `http://${hostname}:5010`;
      }
      return origin;
    } catch {
      return '';
    }
  })();
  const isLocalDevFrontend =
    policyOrigin &&
    (() => {
      try {
        const { hostname, port } = window.location;
        return (
          (hostname === 'localhost' || hostname === '127.0.0.1') &&
          port &&
          port !== '5010'
        );
      } catch {
        return false;
      }
    })();

  const normalizedPrivacyPolicyUrl = normalizePolicyHref({
    rawUrl: privacyPolicyUrl,
    policyOrigin,
  });
  const normalizedTermsOfServiceUrl = normalizePolicyHref({
    rawUrl: termsOfServiceUrl,
    policyOrigin,
  });

  const slug = slugifyBusinessName(businessName);
  const encodedNumber = encodeURIComponent(String(twilioNumber || '').trim());
  const computedTermsUrl =
    encodedNumber
      ? `${policyOrigin}/policies/${slug}/${encodedNumber}/sms-terms`
      : '';
  const computedPrivacyUrl =
    encodedNumber
      ? `${policyOrigin}/policies/${slug}/${encodedNumber}/sms-privacy`
      : '';

  const coercePolicyToBackend = (href) => {
    if (!href || !policyOrigin) return href;
    if (!isLocalDevFrontend) return href;
    try {
      const parsed = new URL(href);
      if (parsed.pathname && parsed.pathname.startsWith('/policies/')) {
        return `${policyOrigin}${parsed.pathname}${parsed.search || ''}${parsed.hash || ''}`;
      }
      return href;
    } catch {
      // If it's a relative path, pin it.
      if (String(href).startsWith('/policies/')) return `${policyOrigin}${href}`;
      return href;
    }
  };

  const effectiveTermsUrl = coercePolicyToBackend(
    normalizedTermsOfServiceUrl || computedTermsUrl,
  );
  const effectivePrivacyUrl = coercePolicyToBackend(
    normalizedPrivacyPolicyUrl || computedPrivacyUrl,
  );

  const hasCustomerPolicyLinks = Boolean(
    effectiveTermsUrl && effectivePrivacyUrl,
  );

  return (
    <Card className='w-full p-5 shadow-lg sm:p-8'>
      {businessName === '' ? (
        <FormSkeleton
          apiError={apiError}
          apiErrorMessage={
            apiErrorMessage
              ? apiErrorMessage
              : 'Server error! Please try again in a few minutes.'
          }
          errorColor={errorColor}
        />
      ) : (
        <>
          {controlTextSize(businessName)}
          <p className='mx-auto max-w-[50ch] whitespace-pre-line text-center text-sm leading-snug text-muted-foreground sm:text-base'>
            {brandPromotionalMessage}
          </p>
          <form noValidate onSubmit={handleSubmit} className='mt-5 w-full'>
            <div className='space-y-3'>
              <div className='grid w-full gap-1.5'>
                <Label htmlFor='name'>Name</Label>
                <Input
                  id='name'
                  name='name'
                  autoFocus
                  className={inputClassName}
                  onChange={handleNameChange}
                  value={name}
                />
              </div>
              {isIncludingPhone ? (
                <div className='grid w-full gap-1.5'>
                  <Label htmlFor='phone'>Mobile phone</Label>
                  <Input
                    id='phone'
                    name='phone'
                    type='tel'
                    className={inputClassName}
                    onChange={handlePhoneChange}
                    value={phoneNumber}
                  />
                </div>
              ) : null}

              {isIncludingEmail ? (
                <div className='grid w-full gap-1.5'>
                  <Label htmlFor='email'>Email Address</Label>
                  <Input
                    id='email'
                    name='email'
                    type='email'
                    className={inputClassName}
                    onChange={handleEmailChange}
                    value={email}
                  />
                </div>
              ) : null}
            </div>

            <div className='mt-4 divide-y rounded-md border px-3'>
              <div className={consentRowClassName}>
                <Checkbox
                  id='supportOptIn'
                  className={consentCheckboxClassName}
                  checked={supportOptIn}
                  onCheckedChange={(checked) => setSupportOptIn(checked === true)}
                />
                <Label htmlFor='supportOptIn' className={consentTextClassName}>
                  <b>Customer support</b>: By checking this box, I agree to
                  receive customer support and transactional/informational
                  text messages from {businessName}. Message frequency
                  varies.{' '}
                  <span className='font-medium'>
                    Message and data rates may apply.
                  </span>{' '}
                  Reply STOP to unsubscribe. Reply HELP for help.
                </Label>
              </div>

              <div className={consentRowClassName}>
                <Checkbox
                  id='marketingOptIn'
                  className={consentCheckboxClassName}
                  checked={marketingOptIn}
                  onCheckedChange={(checked) =>
                    setMarketingOptIn(checked === true)
                  }
                />
                <Label
                  htmlFor='marketingOptIn'
                  className={consentTextClassName}
                >
                  <b>Marketing</b>: By checking this box, I agree to
                  receive marketing/promotional text messages from{' '}
                  {businessName}. Message frequency: 1 message per month.{' '}
                  <span className='font-medium'>
                    Message and data rates may apply.
                  </span>{' '}
                  Reply STOP to unsubscribe. Reply HELP for help.
                </Label>
              </div>

              <div className={consentRowClassName}>
                <Checkbox
                  id='termsAccepted'
                  className={consentCheckboxClassName}
                  checked={termsAccepted}
                  onCheckedChange={(checked) =>
                    setTermsAccepted(checked === true)
                  }
                />
                <Label
                  htmlFor='termsAccepted'
                  className={consentTextClassName}
                >
                  <b>Terms &amp; conditions</b>: I agree to the&nbsp;
                  {hasCustomerPolicyLinks ? (
                    <>
                      <a
                        href={effectiveTermsUrl}
                        className={policyLinkClassName}
                        target='_blank'
                        rel='noopener'
                      >
                        Terms of Service
                      </a>
                      &nbsp;and&nbsp;
                      <a
                        href={effectivePrivacyUrl}
                        className={policyLinkClassName}
                        target='_blank'
                        rel='noopener'
                      >
                        Privacy Policy
                      </a>
                      .
                    </>
                  ) : (
                    <>our Terms of Service and Privacy Policy.</>
                  )}
                </Label>
              </div>
            </div>
            <Button
              type='submit'
              className='my-4 w-full bg-brand font-semibold text-brand-foreground hover:bg-brand/90'
              disabled={loading}
            >
              {loading && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
              Submit
            </Button>
            {formErrorMessage ? (
              <p
                className='-mt-2 mb-4 text-center text-sm font-semibold'
                style={{ color: errorColor }}
              >
                {formErrorMessage}
              </p>
            ) : null}
          </form>
        </>
      )}
    </Card>
  );
}

function controlTextSize(businessName) {
  const sizeClassName =
    businessName.length > 25
      ? 'text-[2rem] sm:text-[2.4rem]'
      : 'text-4xl sm:text-[2.75rem]';

  return (
    <h1
      className={`mb-2.5 break-words text-center font-extrabold leading-[1.1] tracking-tight sm:mb-4 ${sizeClassName}`}
    >
      {businessName}
    </h1>
  );
}

export default CustomerForm;
